import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { layoutChain, truncateLabel } from './chain-layout';
import styles from './WorkflowChain.module.css';

/**
 * The workflow drawn as the claim it makes: maker at the top, each approval step
 * a node on one straight chain, the approved outcome at the bottom — and the
 * rejected outcome OFF the chain to the right, with a red exit leaving every
 * step, because rejection is available at any checkpoint and terminates the
 * rest of the run.
 *
 * ONE COMPONENT, TWO READINGS. The designer renders it with every step 'pending'
 * as a live preview of the draft being edited; the instance detail renders the
 * SAME picture from the case's definition snapshot with decided steps filled in,
 * the waiting step pulsing, and the exit actually taken drawn solid. Sharing the
 * component is what guarantees the promise the designer made and the history the
 * approvers lived are the same picture.
 *
 * All geometry comes from layoutChain() in chain-layout.ts, a pure function with
 * its own tests; this file only draws. THEME-AWARE BY CONSTRUCTION: every colour
 * is a CSS variable via the module stylesheet — the SVG carries no hex anywhere.
 * Accessible as a figure: role="img" with a summary label, plus a <title> on
 * every node so a screen reader walking the picture hears the chain in order.
 */

export type ChainStepStatus = 'pending' | 'current' | 'done' | 'rejected';

export interface ChainStepView {
  title: string;
  /** "Any 2 of Principal, Bursar" — who can pass this step. */
  approverSummary: string;
  status: ChainStepStatus;
  /** Decision line on a decided step: approver name and time. */
  detail?: string;
}

interface WorkflowChainProps {
  steps: readonly ChainStepView[];
  makerLabel: string;
  /** The initiator's name on an instance; absent on the design preview. */
  makerDetail?: string;
  approvedLabel: string;
  rejectedLabel: string;
  /** How the case ended, when it has; colours the outcome nodes. */
  outcome?: 'APPROVED' | 'REJECTED' | 'CANCELLED' | null;
  ariaLabel: string;
}

const TITLE_BUDGET = 30;
const DETAIL_BUDGET = 36;

export function WorkflowChain({
  steps,
  makerLabel,
  makerDetail,
  approvedLabel,
  rejectedLabel,
  outcome = null,
  ariaLabel,
}: WorkflowChainProps) {
  const { t } = useTranslation();
  const uid = useId();
  const layout = layoutChain(steps.length);

  const flowMarker = `wf-flow-${uid}`;
  const rejectMarker = `wf-reject-${uid}`;

  const stepClass = (status: ChainStepStatus): string =>
    ({
      pending: styles.step,
      current: `${styles.step} ${styles.stepCurrent}`,
      done: `${styles.step} ${styles.stepDone}`,
      rejected: `${styles.step} ${styles.stepRejected}`,
    })[status];

  return (
    <svg className={styles.diagram} viewBox={`0 0 ${layout.width} ${layout.height}`} role="img" aria-label={ariaLabel}>
      <title>{ariaLabel}</title>

      <defs>
        {/* Two markers, not one: SVG markers cannot inherit per-edge stroke, so
            the reject arrows carry their own red head. */}
        <marker id={flowMarker} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L8,4 L0,8 z" className={styles.flowArrowHead} />
        </marker>
        <marker id={rejectMarker} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L8,4 L0,8 z" className={styles.rejectArrowHead} />
        </marker>
      </defs>

      {layout.edges.map((edge) => {
        const taken = edge.kind === 'reject' && steps[edge.fromStep]?.status === 'rejected';
        return (
          <line
            key={`${edge.kind}-${edge.fromStep}-${edge.y1}`}
            className={
              edge.kind === 'flow'
                ? styles.flowEdge
                : taken
                  ? `${styles.rejectEdge} ${styles.rejectEdgeTaken}`
                  : styles.rejectEdge
            }
            x1={edge.x1}
            y1={edge.y1}
            x2={edge.x2}
            y2={edge.y2}
            markerEnd={`url(#${edge.kind === 'flow' ? flowMarker : rejectMarker})`}
            aria-hidden="true"
          />
        );
      })}

      {layout.nodes.map((node) => {
        if (node.kind === 'maker') {
          return (
            <g key="maker" className={styles.maker}>
              <title>{makerDetail ? `${makerLabel} — ${makerDetail}` : makerLabel}</title>
              <rect className={styles.makerBox} x={node.x} y={node.y} width={node.width} height={node.height} rx={node.height / 2} />
              <text className={styles.makerLabel} x={node.x + node.width / 2} y={node.y + (makerDetail ? 19 : 28)} textAnchor="middle">
                {truncateLabel(makerLabel, DETAIL_BUDGET)}
              </text>
              {makerDetail && (
                <text className={styles.makerDetail} x={node.x + node.width / 2} y={node.y + 35} textAnchor="middle">
                  {truncateLabel(makerDetail, DETAIL_BUDGET)}
                </text>
              )}
            </g>
          );
        }

        if (node.kind === 'step') {
          const step = steps[node.stepIndex];
          if (!step) return null;
          const stepNo = node.stepIndex + 1;
          const spoken = t('admin.workflows.chain.stepTitle', {
            no: stepNo,
            title: step.title || t('admin.workflows.chain.untitledStep'),
            who: step.approverSummary,
          });
          return (
            <g key={`step-${node.stepIndex}`} className={stepClass(step.status)}>
              <title>{step.detail ? `${spoken} — ${step.detail}` : spoken}</title>
              <rect className={styles.stepBox} x={node.x} y={node.y} width={node.width} height={node.height} rx={8} />
              <circle className={styles.stepNo} cx={node.x + 20} cy={node.y + node.height / 2} r={11} />
              <text className={styles.stepNoText} x={node.x + 20} y={node.y + node.height / 2 + 3.5} textAnchor="middle">
                {stepNo}
              </text>
              <text className={styles.stepTitle} x={node.x + 40} y={node.y + (step.detail ? 18 : 24)}>
                {truncateLabel(step.title || t('admin.workflows.chain.untitledStep'), TITLE_BUDGET)}
              </text>
              <text className={styles.stepWho} x={node.x + 40} y={node.y + (step.detail ? 33 : 42)}>
                {truncateLabel(step.approverSummary, DETAIL_BUDGET)}
              </text>
              {step.detail && (
                <text className={styles.stepDetail} x={node.x + 40} y={node.y + 49}>
                  {truncateLabel(step.detail, DETAIL_BUDGET)}
                </text>
              )}
            </g>
          );
        }

        if (node.kind === 'approved') {
          return (
            <g key="approved" className={outcome === 'APPROVED' ? `${styles.approved} ${styles.approvedReached}` : styles.approved}>
              <title>{approvedLabel}</title>
              <rect className={styles.approvedBox} x={node.x} y={node.y} width={node.width} height={node.height} rx={node.height / 2} />
              <text className={styles.approvedLabel} x={node.x + node.width / 2} y={node.y + node.height / 2 + 4} textAnchor="middle">
                {truncateLabel(approvedLabel, DETAIL_BUDGET)}
              </text>
            </g>
          );
        }

        return (
          <g key="rejected" className={outcome === 'REJECTED' ? `${styles.rejected} ${styles.rejectedReached}` : styles.rejected}>
            <title>{rejectedLabel}</title>
            <rect className={styles.rejectedBox} x={node.x} y={node.y} width={node.width} height={node.height} rx={node.height / 2} />
            <text className={styles.rejectedLabel} x={node.x + node.width / 2} y={node.y + node.height / 2 + 4} textAnchor="middle">
              {truncateLabel(rejectedLabel, 18)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
